/**
 * API client service with fetch wrapper and authentication.
 *
 * Provides a consistent interface for making API requests with:
 * - Automatic token injection
 * - Request/response interceptors
 * - Error handling
 * - Request retry logic
 */

/**
 * API configuration
 */
const env = import.meta.env || {};
const config = {
  baseUrl: env.VITE_API_BASE_URL || '/api',
  timeout: parseInt(env.VITE_API_TIMEOUT || '300000', 10),
  retryAttempts: parseInt(env.VITE_API_RETRY_ATTEMPTS || '3', 10),
  retryDelay: parseInt(env.VITE_API_RETRY_DELAY || '1000', 10),
};

/**
 * Get the API base URL
 * @returns {string} The API base URL from environment config
 */
export function getApiBaseUrl() {
  return config.baseUrl;
}

/**
 * HTTP methods
 * @type {Object}
 */
export const HttpMethod = {
  GET: 'GET',
  POST: 'POST',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

/**
 * Request interceptors
 * @type {Function[]}
 */
const requestInterceptors = [];

/**
 * Response interceptors
 * @type {Function[]}
 */
const responseInterceptors = [];

/**
 * Add a request interceptor
 * @param {Function} interceptor - Function that receives and returns request config
 */
export function addRequestInterceptor(interceptor) {
  requestInterceptors.push(interceptor);
}

/**
 * Add a response interceptor
 * @param {Function} interceptor - Function that receives and returns response
 */
export function addResponseInterceptor(interceptor) {
  responseInterceptors.push(interceptor);
}

/**
 * Get the current auth token
 * @returns {string|null}
 */
export function getAuthToken() {
  // Try to get token from auth service
  try {
    const authData = localStorage.getItem('auth');
    if (authData) {
      const { accessToken, expiresAt } = JSON.parse(authData);

      // Parse expiration date safely
      let isExpired = true;
      if (expiresAt) {
        try {
          const expiresDate = new Date(expiresAt);
          // Check if date is valid (not NaN) and in the future
          if (!isNaN(expiresDate.getTime()) && expiresDate > new Date()) {
            isExpired = false;
          }
        } catch (e) {
          console.warn('Failed to parse expiresAt in getAuthToken:', e);
        }
      }

      // Return token if not expired
      if (!isExpired && accessToken) {
        return accessToken;
      }

      // Token expired - clear auth data and return null
      // Don't fall back to dev token when user had a real session
      localStorage.removeItem('auth');
      return null;
    }
  } catch (e) {
    console.warn('Failed to get auth token:', e);
  }

  // Development fallback - only create dev token if NO auth data exists
  // This allows testing without logging in, but won't interfere with real sessions
  if (window.location.hostname === 'localhost') {
    const header = btoa(JSON.stringify({ alg: 'none', typ: 'JWT' })).replace(/=/g, '');
    const payload = btoa(JSON.stringify({
      sub: 'dev_user_123',
      oid: 'dev_user_123',
      email: 'dev@example.com',
      name: 'Dev User',
      extension_OrganizationId: 'org_001',
      extension_Role: 'admin',
      iat: Math.floor(Date.now() / 1000),
      exp: Math.floor(Date.now() / 1000) + 86400,
    })).replace(/=/g, '');
    return `${header}.${payload}.`;
  }

  return null;
}

/**
 * Build request headers
 * @param {Object} customHeaders - Additional headers
 * @returns {Headers}
 */
function buildHeaders(customHeaders = {}) {
  const headers = new Headers({
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...customHeaders,
  });

  const token = getAuthToken();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  return headers;
}

/**
 * Apply request interceptors
 * @param {Object} requestConfig - Request configuration
 * @returns {Object} Modified request configuration
 */
async function applyRequestInterceptors(requestConfig) {
  let config = requestConfig;
  for (const interceptor of requestInterceptors) {
    config = await interceptor(config);
  }
  return config;
}

/**
 * Apply response interceptors
 * @param {Response} response - Fetch response
 * @returns {Response} Modified response
 */
async function applyResponseInterceptors(response) {
  let res = response;
  for (const interceptor of responseInterceptors) {
    res = await interceptor(res);
  }
  return res;
}

/**
 * API error class
 */
export class ApiError extends Error {
  constructor(message, status, code, details = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  static fromResponse(response, body) {
    const error = body?.error || {};
    return new ApiError(
      error.message || response.statusText || 'Request failed',
      response.status,
      error.code || 'UNKNOWN_ERROR',
      error.details
    );
  }
}

/**
 * Sleep for a given duration
 * @param {number} ms - Duration in milliseconds
 * @returns {Promise}
 */
function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Check if error is retryable
 * @param {Error} error - The error to check
 * @returns {boolean}
 */
function isRetryable(error) {
  if (error instanceof ApiError) {
    // Retry on server errors and specific client errors
    return error.status >= 500 || error.status === 429 || error.status === 408;
  }
  // Retry on network errors
  return error.name === 'TypeError' || error.name === 'AbortError';
}

/**
 * Make an API request
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Request options
 * @returns {Promise<any>} Response data
 */
async function request(endpoint, options = {}) {
  const {
    method = HttpMethod.GET,
    body = null,
    headers = {},
    params = null,
    timeout = config.timeout,
    retry = true,
    retryAttempts = config.retryAttempts,
  } = options;

  // Build URL with query params
  let url = `${config.baseUrl}${endpoint}`;
  if (params) {
    const searchParams = new URLSearchParams(params);
    url += `?${searchParams.toString()}`;
  }

  // Build request config
  let requestConfig = {
    method,
    headers: buildHeaders(headers),
    body: body ? JSON.stringify(body) : null,
  };

  // Apply request interceptors
  requestConfig = await applyRequestInterceptors(requestConfig);

  // Create abort controller for timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  requestConfig.signal = controller.signal;

  let lastError = null;
  let attempts = retry ? retryAttempts : 1;

  while (attempts > 0) {
    try {
      let response = await fetch(url, requestConfig);

      // Apply response interceptors
      response = await applyResponseInterceptors(response);

      clearTimeout(timeoutId);

      // Handle 204 No Content - return early without parsing body
      if (response.status === 204) {
        return null;
      }

      // Parse response body
      const contentType = response.headers.get('content-type');
      let data;

      if (contentType?.includes('application/json')) {
        data = await response.json();
      } else if (contentType?.includes('text/')) {
        data = await response.text();
      } else if (response.status !== 204 && contentType) {
        data = await response.blob();
      } else {
        data = null;
      }

      // Check for errors
      if (!response.ok) {
        throw ApiError.fromResponse(response, data);
      }

      return data;
    } catch (error) {
      lastError = error;
      attempts--;

      if (attempts > 0 && retry && isRetryable(error)) {
        // Exponential backoff
        const delay = config.retryDelay * Math.pow(2, config.retryAttempts - attempts);
        // Only log retries in development and not for network errors (server down)
        if (error.name !== 'TypeError') {
          console.warn(`Request failed, retrying in ${delay}ms...`, error);
        }
        await sleep(delay);
      } else {
        break;
      }
    }
  }

  clearTimeout(timeoutId);
  throw lastError;
}

/**
 * API client methods
 */
export const api = {
  /**
   * GET request
   * @param {string} endpoint - API endpoint
   * @param {Object} params - Query parameters
   * @param {Object} options - Additional options
   * @returns {Promise<any>}
   */
  get(endpoint, params = null, options = {}) {
    return request(endpoint, { ...options, method: HttpMethod.GET, params });
  },

  /**
   * POST request
   * @param {string} endpoint - API endpoint
   * @param {Object} body - Request body
   * @param {Object} options - Additional options
   * @returns {Promise<any>}
   */
  post(endpoint, body = null, options = {}) {
    return request(endpoint, { ...options, method: HttpMethod.POST, body });
  },

  /**
   * PUT request
   * @param {string} endpoint - API endpoint
   * @param {Object} body - Request body
   * @param {Object} options - Additional options
   * @returns {Promise<any>}
   */
  put(endpoint, body = null, options = {}) {
    return request(endpoint, { ...options, method: HttpMethod.PUT, body });
  },

  /**
   * PATCH request
   * @param {string} endpoint - API endpoint
   * @param {Object} body - Request body
   * @param {Object} options - Additional options
   * @returns {Promise<any>}
   */
  patch(endpoint, body = null, options = {}) {
    return request(endpoint, { ...options, method: HttpMethod.PATCH, body });
  },

  /**
   * DELETE request
   * @param {string} endpoint - API endpoint
   * @param {Object} options - Additional options
   * @returns {Promise<any>}
   */
  delete(endpoint, options = {}) {
    return request(endpoint, { ...options, method: HttpMethod.DELETE });
  },

  /**
   * Upload file(s)
   * @param {string} endpoint - API endpoint
   * @param {FormData} formData - Form data with files
   * @param {Object} options - Additional options
   * @returns {Promise<any>}
   */
  async upload(endpoint, formData, options = {}) {
    const { onProgress, ...restOptions } = options;

    // Remove Content-Type to let browser set it with boundary
    const headers = buildHeaders({});
    headers.delete('Content-Type');

    const url = `${config.baseUrl}${endpoint}`;

    // Use XMLHttpRequest for progress tracking
    if (onProgress) {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', url);

        // Set headers
        const token = getAuthToken();
        if (token) {
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }

        // Progress handler
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const progress = (event.loaded / event.total) * 100;
            onProgress(progress, event.loaded, event.total);
          }
        });

        // Complete handler
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch {
              resolve(xhr.responseText);
            }
          } else {
            let error;
            try {
              const body = JSON.parse(xhr.responseText);
              error = new ApiError(
                body.error?.message || xhr.statusText,
                xhr.status,
                body.error?.code || 'UPLOAD_ERROR',
                body.error?.details
              );
            } catch {
              error = new ApiError(xhr.statusText, xhr.status, 'UPLOAD_ERROR');
            }
            reject(error);
          }
        });

        // Error handler
        xhr.addEventListener('error', () => {
          reject(new ApiError('Upload failed', 0, 'NETWORK_ERROR'));
        });

        xhr.send(formData);
      });
    }

    // Standard fetch for non-progress uploads
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });

    const contentType = response.headers.get('content-type');
    const data = contentType?.includes('application/json')
      ? await response.json()
      : await response.text();

    if (!response.ok) {
      throw ApiError.fromResponse(response, data);
    }

    return data;
  },

  /**
   * Download file
   * @param {string} endpoint - API endpoint
   * @param {string} filename - Suggested filename
   * @param {Object} options - Additional options
   */
  async download(endpoint, filename, options = {}) {
    const url = `${config.baseUrl}${endpoint}`;
    const headers = buildHeaders({});

    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const data = await response.json();
      throw ApiError.fromResponse(response, data);
    }

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);

    // Trigger download
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Cleanup
    URL.revokeObjectURL(objectUrl);
  },
};

// Add auth refresh interceptor
addResponseInterceptor(async (response) => {
  if (response.status === 401) {
    // Token expired or invalid
    const authData = localStorage.getItem('auth');
    if (authData) {
      try {
        const { refreshToken } = JSON.parse(authData);
        if (refreshToken) {
          // Dispatch token refresh event
          window.dispatchEvent(new CustomEvent('auth:token-expired'));
          return response;
        }
      } catch (e) {
        console.warn('Failed to handle 401:', e);
      }
    }

    // No auth data or no refresh token - redirect to login
    if (!window.location.pathname.includes('/login') &&
        !window.location.pathname.includes('/register') &&
        !window.location.pathname.includes('/accept-invitation')) {
      localStorage.removeItem('auth');
      window.location.href = '/#/login';
    }
  }
  return response;
});

// Add user status interceptor for 403 errors
addResponseInterceptor(async (response) => {
  if (response.status === 403) {
    try {
      // Clone the response to read it without consuming the original
      const clonedResponse = response.clone();
      const data = await clonedResponse.json();

      // Check for specific user status error codes
      const detail = data?.detail || data?.error || {};
      const code = typeof detail === 'object' ? detail.code : null;

      if (code && ['USER_DEACTIVATED', 'USER_SUSPENDED', 'USER_PENDING_VERIFICATION', 'USER_PENDING_APPROVAL'].includes(code)) {
        const message = detail.message || 'Your account is not active';
        const reason = detail.reason;

        // Clear auth data
        localStorage.removeItem('auth');

        // Dispatch custom event with error details
        window.dispatchEvent(new CustomEvent('auth:account-inactive', {
          detail: { code, message, reason }
        }));

        // If we're not already on the login page, redirect there
        if (!window.location.pathname.includes('/login')) {
          // Store the error message for the login page to display
          sessionStorage.setItem('auth_error', JSON.stringify({ code, message, reason }));
          window.location.href = '/login';
        }
      }
    } catch (e) {
      // Ignore parsing errors - let the normal error handling take care of it
    }
  }
  return response;
});

// Export as both 'api' and 'apiClient' for compatibility
export { api as apiClient };
export default api;
